import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const StoreCtx = createContext(null);
const KEY = "cvz_cart_v1";
export const toPayload = (items) => items.map(({ product_id, grams, qty, options }) => ({ product_id, grams, qty, options: options || {} }));

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || [];
  } catch {
    return [];
  }
};

export function StoreProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState(load);
  const [quote, setQuote] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const timer = useRef(null);

  const refreshSettings = useCallback(() => api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {}), []);
  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
    clearTimeout(timer.current);
    if (!items.length) {
      setQuote(null);
      return;
    }
    timer.current = setTimeout(() => {
      api.post("/cart/quote", { items: toPayload(items) })
        .then((r) => setQuote(r.data))
        .catch(() => {});
    }, 250);
  }, [items]);

  const addItem = useCallback((p, gramsVal, qty = 1, unitPrice, options = {}) => {
    const key = `${p.id}:${gramsVal || "u"}:${Object.values(options).join("|")}`;
    setItems((cur) => {
      const ex = cur.find((i) => i.key === key);
      if (ex) return cur.map((i) => (i.key === key ? { ...i, qty: Math.min(999, i.qty + qty) } : i));
      return [...cur, { key, product_id: p.id, name: p.name, slug: p.slug, image: p.images?.[0], type: p.type, grams: gramsVal || null, options, qty, unit_price: unitPrice }];
    });
    setCartOpen(true);
  }, []);
  const setQty = useCallback((key, qty) => setItems((cur) => (qty < 1 ? cur.filter((i) => i.key !== key) : cur.map((i) => (i.key === key ? { ...i, qty: Math.min(999, qty) } : i)))), []);
  const removeItem = useCallback((key) => setItems((cur) => cur.filter((i) => i.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);

  const localSubtotal = items.reduce((s, i) => s + i.unit_price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);
  const value = useMemo(
    () => ({ settings, refreshSettings, items, quote, addItem, setQty, removeItem, clear, localSubtotal, count, cartOpen, setCartOpen }),
    [settings, refreshSettings, items, quote, addItem, setQty, removeItem, clear, localSubtotal, count, cartOpen],
  );
  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}

export const useStore = () => useContext(StoreCtx);
